import type { Block, DeletionReceipt, SpaceEntry, ConnectionSelection, DeleteResult, DocumentState } from './types.js'
import { formatDateStamp, formatTimeStamp, generateId, seedClock } from './id.js'
import { cloneBlock, cloneSpaceEntry, createDeletionReceipt, isLiveBlock, isDeletionReceipt } from './lib/spaceCodec.js'
import { SNIPPET_BOUNDARY_CHARS, SNIPPET_TRIGGERS } from './constants.js'

export type MergeTextBlocksResult =
  | { merged: true, destinationId: string, sourceIds: string[] }
  | { merged: false, reason: 'destination-missing' | 'not-enough-blocks' }

export interface SnippetHit {
  start: number
  trigger: string
  stamp: string
}

const SNIPPET_STAMPS: Record<string, (now: Date) => string> = {
  '!D': now => formatDateStamp(now),
  '!T': now => formatTimeStamp(now),
  '!DT': now => `${formatDateStamp(now)} ${formatTimeStamp(now)}`,
}

// A trigger expands only when it ends exactly at the caret and starts at the
// text start or after a boundary character, so prose like "Danke!Der" never fires.
export function findSnippetAtCaret(text: string, caretIndex: number, now: Date): SnippetHit | null {
  if (caretIndex < SNIPPET_TRIGGERS[SNIPPET_TRIGGERS.length - 1].length || caretIndex > text.length) return null
  const head = text.slice(0, caretIndex)
  for (const trigger of SNIPPET_TRIGGERS) {
    if (!head.endsWith(trigger)) continue
    const start = caretIndex - trigger.length
    if (start > 0 && !SNIPPET_BOUNDARY_CHARS.includes(head[start - 1])) continue
    return { start, trigger, stamp: SNIPPET_STAMPS[trigger](now) }
  }
  return null
}

export class Document {
  private state: DocumentState = {
    blocks: [],
    selectedBlockIds: [],
    selectedConnections: [],
  }

  private receipts = new Map<string, DeletionReceipt>()

  hydrate(entries: SpaceEntry[]): void {
    const normalized = entries.map(cloneSpaceEntry)
    seedClock(normalized)
    this.state.blocks = normalized.filter(isLiveBlock).map(block => ({ ...block, meta: block.meta ?? {} }))
    this.receipts = new Map(normalized.filter(isDeletionReceipt).map(receipt => [receipt.ided[0], receipt]))
    this.state.selectedBlockIds = []
    this.state.selectedConnections = []
  }

  getStateSnapshot(): SpaceEntry[] {
    return [...this.state.blocks, ...this.receipts.values()].map(cloneSpaceEntry)
  }

  // History restores content as new authored events, never rewinds the sync clock.
  restoreSnapshot(snapshot: SpaceEntry[]): void {
    const current = this.getStateSnapshot()
    const target = snapshot.map(cloneSpaceEntry)
    seedClock([...current, ...target])
    const currentById = new Map(current.map(entry => [entry.ided[0], entry]))
    const next = new Map<string, SpaceEntry>(this.receipts)
    const targetLive = target.filter(isLiveBlock)
    const targetIds = new Set(targetLive.map(block => block.ided[0]))
    for (const block of this.state.blocks) {
      if (!targetIds.has(block.ided[0])) {
        next.set(block.ided[0], createDeletionReceipt(block.ided[0], generateId()))
      }
    }
    for (const receipt of target.filter(isDeletionReceipt)) {
      const existing = next.get(receipt.ided[0])
      if (!existing || existing.ided[1] < receipt.ided[1]) next.set(receipt.ided[0], receipt)
    }
    for (const block of targetLive) {
      const existing = currentById.get(block.ided[0])
      const restored = cloneBlock(block)
      restored.ided[1] = existing && isLiveBlock(existing) && sameBlockContent(existing, block)
        ? existing.ided[1]
        : generateId()
      next.set(block.ided[0], restored)
    }
    this.hydrate([...next.values()])
  }

  getBlocks(): Block[] {
    return [...this.state.blocks]
  }

  getBlock(id: string): Block | undefined {
    return this.state.blocks.find(block => block.ided[0] === id)
  }

  createBlock(text: string, x: number, y: number, width: number): string {
    const creationId = generateId()
    const block: Block = {
      text,
      meta: {},
      xyzw: [x, y, 0, width],
      ided: [creationId, creationId],
      link: []
    }
    
    this.state.blocks.push(block)
    return creationId
  }

  updateBlockText(id: string, text: string): boolean {
    const block = this.getBlock(id)
    if (!block) return false
    
    block.text = text
    block.ided[1] = generateId() // Update last-edit timestamp
    return true
  }

  updateBlockPosition(id: string, x: number, y: number): boolean {
    const block = this.getBlock(id)
    if (!block) return false
    
    block.xyzw[0] = x
    block.xyzw[1] = y
    block.ided[1] = generateId()
    return true
  }

  updateBlockWidth(id: string, width: number): boolean {
    const block = this.getBlock(id)
    if (!block) return false

    block.xyzw[3] = width
    block.ided[1] = generateId()
    return true
  }

  // One absolute width for many blocks; blocks already at that width keep
  // their edit id untouched so no-op resizes never bump the sync clock.
  updateBlocksWidth(ids: string[], width: number): boolean {
    let changed = false
    ids.forEach(id => {
      const block = this.getBlock(id)
      if (!block || block.xyzw[3] === width) return
      changed = this.updateBlockWidth(id, width) || changed
    })
    return changed
  }

  addLink(sourceId: string, targetId: string): boolean {
    if (sourceId === targetId) return false
    const source = this.getBlock(sourceId)
    const target = this.getBlock(targetId)
    if (!source || !target) return false
    if (source.link.includes(targetId)) return false
    source.link = [...source.link, targetId]
    source.ided[1] = generateId()
    return true
  }

  removeLink(sourceId: string, targetId: string, opts?: { suppressSelectionClear?: boolean }): boolean {
    const source = this.getBlock(sourceId)
    if (!source) return false
    const nextLinks = source.link.filter(id => id !== targetId)
    if (nextLinks.length === source.link.length) return false
    source.link = nextLinks
    source.ided[1] = generateId()
    if (!opts?.suppressSelectionClear && this.state.selectedConnections.length) {
      this.state.selectedConnections = this.state.selectedConnections.filter(sel => !(sel.sourceId === sourceId && sel.targetId === targetId))
    }
    return true
  }

  countBlockConnections(id: string): number {
    const block = this.getBlock(id)
    if (!block) return 0

    let count = block.link.length
    this.state.blocks.forEach(source => {
      if (source.ided[0] === id) return
      if (source.link.includes(id)) {
        count += 1
      }
    })
    return count
  }

  disconnectBlock(id: string): number {
    const block = this.getBlock(id)
    if (!block) return 0

    let removedCount = 0

    const outgoingTargets = [...block.link]
    outgoingTargets.forEach(targetId => {
      if (this.removeLink(id, targetId, { suppressSelectionClear: true })) {
        removedCount += 1
      }
    })

    this.state.blocks.forEach(source => {
      if (source.ided[0] === id) return
      if (this.removeLink(source.ided[0], id, { suppressSelectionClear: true })) {
        removedCount += 1
      }
    })

    if (this.state.selectedConnections.length) {
      this.state.selectedConnections = this.state.selectedConnections.filter(sel => sel.sourceId !== id && sel.targetId !== id)
    }

    return removedCount
  }

  mergeTextBlocks(destinationId: string, sourceIds: string[]): MergeTextBlocksResult {
    const destination = this.getBlock(destinationId)
    if (!destination) {
      return { merged: false, reason: 'destination-missing' }
    }

    const seen = new Set<string>([destinationId])
    const sources: Block[] = []
    sourceIds.forEach(id => {
      if (seen.has(id)) return
      seen.add(id)
      const block = this.getBlock(id)
      if (!block) return
      sources.push(block)
    })

    if (!sources.length) {
      return { merged: false, reason: 'not-enough-blocks' }
    }

    const sourceIdSet = new Set(sources.map(block => block.ided[0]))
    const mergedSet = new Set<string>([destinationId, ...sourceIdSet])
    const destinationLinks: string[] = []
    const pushDestinationLink = (targetId: string): void => {
      const mapped = sourceIdSet.has(targetId) ? destinationId : targetId
      if (mapped === destinationId) return
      if (destinationLinks.includes(mapped)) return
      destinationLinks.push(mapped)
    }

    destination.link.forEach(pushDestinationLink)
    sources.forEach(source => {
      source.link.forEach(pushDestinationLink)
    })

    const editId = generateId()
    const textParts = [destination, ...sources]
      .map(block => block.text)
      .filter(text => text.length > 0)
    destination.text = textParts.join('\n\n')
    destination.link = destinationLinks
    destination.ided[1] = editId

    this.state.blocks.forEach(block => {
      const blockId = block.ided[0]
      if (mergedSet.has(blockId)) return
      let changed = false
      const nextLinks: string[] = []
      block.link.forEach(targetId => {
        const mapped = sourceIdSet.has(targetId) ? destinationId : targetId
        if (mapped === blockId) {
          changed = true
          return
        }
        if (nextLinks.includes(mapped)) {
          changed = true
          return
        }
        if (mapped !== targetId) changed = true
        nextLinks.push(mapped)
      })
      if (!changed) return
      block.link = nextLinks
      block.ided[1] = generateId()
    })

    sources.forEach(source => {
      this.deleteBlock(source.ided[0])
    })
    this.selectBlock(destinationId)

    return {
      merged: true,
      destinationId,
      sourceIds: sources.map(block => block.ided[0])
    }
  }

  deleteBlock(id: string): boolean {
    const index = this.state.blocks.findIndex(block => block.ided[0] === id)
    if (index === -1) return false

    this.receipts.set(id, createDeletionReceipt(id, generateId()))
    this.state.blocks.splice(index, 1)

    if (this.state.selectedBlockIds.length) {
      this.state.selectedBlockIds = this.state.selectedBlockIds.filter(selId => selId !== id)
    }
    if (this.state.selectedConnections.length) {
      this.state.selectedConnections = this.state.selectedConnections.filter(({ sourceId, targetId }) => sourceId !== id && targetId !== id)
    }
    
    return true
  }

  selectBlock(id: string): void {
    if (!this.getBlock(id)) return
    this.state.selectedBlockIds = [id]
    this.state.selectedConnections = []
  }

  toggleBlockSelection(id: string): void {
    if (!this.getBlock(id)) return
    const existing = this.state.selectedBlockIds
    const idx = existing.indexOf(id)
    if (idx === -1) {
      this.state.selectedBlockIds = [...existing, id]
    } else {
      const next = [...existing]
      next.splice(idx, 1)
      this.state.selectedBlockIds = next
    }
  }

  setPrimarySelection(id: string): void {
    const idx = this.state.selectedBlockIds.indexOf(id)
    if (idx === -1 || idx === 0) return
    const next = [...this.state.selectedBlockIds]
    next.splice(idx, 1)
    next.unshift(id)
    this.state.selectedBlockIds = next
  }

  getSelectedBlockId(): string | undefined {
    return this.state.selectedBlockIds[0]
  }

  getSelectedBlockIds(): string[] {
    return [...this.state.selectedBlockIds]
  }

  isBlockSelected(id: string): boolean {
    return this.state.selectedBlockIds.includes(id)
  }

  selectConnection(sourceId: string, targetId: string): void {
    this.state.selectedConnections = [{ sourceId, targetId }]
    this.state.selectedBlockIds = []
  }

  toggleConnectionSelection(sourceId: string, targetId: string): void {
    const existingIdx = this.state.selectedConnections.findIndex(sel => sel.sourceId === sourceId && sel.targetId === targetId)
    if (existingIdx === -1) {
      this.state.selectedConnections = [...this.state.selectedConnections, { sourceId, targetId }]
    } else {
      const next = [...this.state.selectedConnections]
      next.splice(existingIdx, 1)
      this.state.selectedConnections = next
    }
  }

  getSelectedConnection(): ConnectionSelection | undefined {
    const sel = this.state.selectedConnections[0]
    return sel ? { ...sel } : undefined
  }

  getSelectedConnections(): ConnectionSelection[] {
    return this.state.selectedConnections.map(sel => ({ ...sel }))
  }

  getLastEditedBlock(): Block | undefined {
    if (!this.state.blocks.length) return undefined
    return this.state.blocks.reduce<Block | undefined>((best, block) => {
      if (!best) return block
      const edit = block.ided[1]
      const bestEdit = best.ided[1]
      if (edit > bestEdit) return block
      if (edit < bestEdit) return best
      // Tie-break on creation id to keep ordering deterministic
      return block.ided[0] > best.ided[0] ? block : best
    }, undefined)
  }

  setSelection(blockIds: string[], connections: ConnectionSelection[]): void {
    const seenBlocks = new Set<string>()
    const validBlocks: string[] = []
    blockIds.forEach(id => {
      if (seenBlocks.has(id)) return
      if (!this.getBlock(id)) return
      seenBlocks.add(id)
      validBlocks.push(id)
    })

    const seenConnections = new Set<string>()
    const validConnections: ConnectionSelection[] = []
    connections.forEach(({ sourceId, targetId }) => {
      const key = `${sourceId}->${targetId}`
      if (seenConnections.has(key)) return
      const source = this.getBlock(sourceId)
      const target = this.getBlock(targetId)
      if (!source || !target) return
      if (!source.link.includes(targetId)) return
      seenConnections.add(key)
      validConnections.push({ sourceId, targetId })
    })

    this.state.selectedBlockIds = validBlocks
    this.state.selectedConnections = validConnections
  }

  clearConnectionSelection(): void {
    this.state.selectedConnections = []
  }

  clearSelection(): void {
    this.state.selectedBlockIds = []
    this.state.selectedConnections = []
  }

  deleteSelection(): DeleteResult {
    const connections = [...this.state.selectedConnections]
    if (connections.length) {
      let removedAny = false
      connections.forEach(({ sourceId, targetId }) => {
        const removed = this.removeLink(sourceId, targetId, { suppressSelectionClear: true })
        removedAny = removed || removedAny
      })
      this.state.selectedConnections = []
      if (removedAny) {
        return { deleted: true, kind: 'connection' }
      }
    }
    const blockIds = [...this.state.selectedBlockIds]
    if (blockIds.length) {
      let removed = false
      blockIds.forEach(id => {
        removed = this.deleteBlock(id) || removed
      })
      if (removed) {
        this.state.selectedBlockIds = []
        return { deleted: true, kind: 'block' }
      }
    }
    return { deleted: false, kind: 'none' }
  }

}

function sameBlockContent(a: Block, b: Block): boolean {
  const metaKeys = new Set([...Object.keys(a.meta ?? {}), ...Object.keys(b.meta ?? {})])
  return a.text === b.text
    && a.xyzw.every((value, index) => value === b.xyzw[index])
    && a.link.length === b.link.length
    && a.link.every((value, index) => value === b.link[index])
    && [...metaKeys].every(key => a.meta?.[key] === b.meta?.[key])
}
